import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { ImageModule } from '@df/module-image/image.module';
import { CustomerModel } from '@df/session/api/customer.model';
import { ButtonsOptions, ColorOptions, RatioOptions, TextStyleOptions } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
import { CountdownComponent } from '@df/ui/display/countdown.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { NTextOverlay, TextOverlayComponent } from 'projects/app-default/src/app/common/text-overlay/text-overlay.component';
import { defaultImage } from 'projects/app-default/src/app/icms.config';
import { EHorizontalAlignmentVariant, EVerticalAlignmentVariant, IPageComponentCountdownBannerData } from './countdown-banner.interface';

export namespace NCountDownBanner {
    export const BLOCK_ID = 'countdown-banner';
    export interface IMinibasketData extends Catalog.IBlockData<IPageComponentCountdownBannerData> {}
}

@Component({
    selector: 'cms-countdown-banner',
    templateUrl: './countdown-banner.html',
    styleUrls: ['./countdown-banner.scss'],
    standalone: true,
    imports: [
        HrefComponent,
        ICmsClientModule,
        ImageModule,
        NgClass,
        ShellComponent,
        TextOverlayComponent,
        TranslationModule,
        CountdownComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageComponentCountdownBannerComponent>({
    id: NCountDownBanner.BLOCK_ID,
    name: 'Countdown Banner',
    tags: ['countdown', 'canner'],
    block: {
        url: NCountDownBanner.BLOCK_ID
    },
    fields: [
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.toggle,
            key: 'limitVisibility',
            title: 'Limit visibility for customer groups',
            default: false
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'eligibleGroupsIds',
            title: 'Visible for customer groups',
            description: 'Comma separated list of eligible groups',
            canShow: (data: IPageComponentCountdownBannerData) => !!data?.limitVisibility
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-image',
            title: 'Image'
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.image,
            key: 'image',
            title: 'Image',
            required: true,
            default: defaultImage
        },
        <NICmsContentField.IChoiceField>{
            key: 'ratio',
            type: NICmsContentField.EFieldType.choice,
            title: 'Image Ratio',
            options: RatioOptions,
            default: 'ratio-1-1'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'link',
            title: 'Link'
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-text',
            title: 'Text'
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.textarea,
            key: 'title',
            title: 'Title',
            required: true,
            default: 'Coming Soon'
        },
        <NICmsContentField.IChoiceField>{
            key: 'titleStyle',
            type: NICmsContentField.EFieldType.choice,
            title: 'Title Style',
            options: TextStyleOptions,
            default: 'h1'
        },
        <NICmsContentField.IChoiceField>{
            key: 'titleColor',
            type: NICmsContentField.EFieldType.choice,
            title: 'Title Colour',
            previewMode: 'color',
            options: ColorOptions,
            default: 'col-b'
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.textarea,
            key: 'subtitle',
            title: 'Subtitle'
        },
        <NICmsContentField.IChoiceField>{
            key: 'subtitleStyle',
            type: NICmsContentField.EFieldType.choice,
            title: 'Subtitle Style',
            options: TextStyleOptions,
            default: 'h2'
        },
        <NICmsContentField.IChoiceField>{
            key: 'subtitleColor',
            type: NICmsContentField.EFieldType.choice,
            title: 'Subtitle Colour',
            previewMode: 'color',
            options: ColorOptions,
            default: 'col-b'
        },
        <NICmsContentField.IChoiceField>{
            key: 'horizontalAlignment',
            title: 'Horizontal Alignment',
            type: NICmsContentField.EFieldType.choice,
            options: [
                { key: EHorizontalAlignmentVariant.left, title: 'left' },
                { key: EHorizontalAlignmentVariant.center, title: 'center' },
                { key: EHorizontalAlignmentVariant.right, title: 'right' }
            ],
            default: EHorizontalAlignmentVariant.left
        },
        <NICmsContentField.IChoiceField>{
            key: 'verticalAlignment',
            title: 'Vertical Alignment',
            type: NICmsContentField.EFieldType.choice,
            options: [
                { key: EVerticalAlignmentVariant.top, title: 'top' },
                { key: EVerticalAlignmentVariant.middle, title: 'middle' },
                { key: EVerticalAlignmentVariant.bottom, title: 'bottom' }
            ],
            default: EVerticalAlignmentVariant.top
        },
        {
            type: NICmsContentField.EFieldType.toggle,
            key: 'compact',
            title: 'Compact Content',
            description: 'Reduce the amount of space between the title, subtitle and countdown',
            default: false
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-countdown',
            title: 'Countdown'
        },
        <NICmsContentField.IChoiceField>{
            key: 'countdownStyle',
            type: NICmsContentField.EFieldType.choice,
            title: 'Countdown Style',
            description: 'The style of the countdown timer numbers',
            options: TextStyleOptions,
            default: 'h1'
        },
        <NICmsContentField.IChoiceField>{
            key: 'countdownColor',
            type: NICmsContentField.EFieldType.choice,
            title: 'Countdown Colour',
            description: 'The colour of the countdown timer numbers',
            previewMode: 'color',
            options: ColorOptions,
            default: 'col-1'
        },
        <NICmsContentField.IChoiceField>{
            key: 'countdownTextColor',
            type: NICmsContentField.EFieldType.choice,
            title: 'Countdown Text Colour',
            description: 'The colour of the text under the countdown',
            previewMode: 'color',
            options: ColorOptions,
            default: 'col-1'
        },
        <NICmsContentField.IDatetimeField>{
            key: 'endDate',
            type: NICmsContentField.EFieldType.datetime,
            title: 'End date',
            dateTime: true
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-button',
            title: 'Button'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'ctaText',
            title: 'CTA Text'
        },
        <NICmsContentField.IChoiceField>{
            key: 'ctaStyle',
            type: NICmsContentField.EFieldType.choice,
            title: 'CTA Style',
            options: ButtonsOptions,
            default: 'button-inline-1'
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-after-countdown',
            title: 'After Countdown'
        },
        {
            key: 'linkAfterCountdown',
            type: NICmsContentField.EFieldType.text,
            title: 'Link'
        },
        {
            key: 'textAfterCountdown',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Text'
        },
        <NICmsContentField.IChoiceField>{
            key: 'textStyleAfterCountdown',
            type: NICmsContentField.EFieldType.choice,
            title: 'Text Style',
            options: TextStyleOptions,
            default: 'h2'
        },
        <NICmsContentField.IChoiceField>{
            key: 'textColorAfterCountdown',
            type: NICmsContentField.EFieldType.choice,
            title: 'Text Color',
            previewMode: 'color',
            options: ColorOptions,
            default: 'col-b'
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.textarea,
            key: 'subtitleAfterCountdown',
            title: 'Subtitle'
        },
        <NICmsContentField.IChoiceField>{
            key: 'subtitleStyleAfterCountdown',
            type: NICmsContentField.EFieldType.choice,
            title: 'Subtitle Style',
            options: TextStyleOptions,
            default: 'h2'
        },
        <NICmsContentField.IChoiceField>{
            key: 'subtitleColorAfterCountdown',
            type: NICmsContentField.EFieldType.choice,
            title: 'Subtitle Colour',
            previewMode: 'color',
            options: ColorOptions,
            default: 'col-b'
        },
        {
            key: 'ctaTextAfterCountdown',
            type: NICmsContentField.EFieldType.text,
            title: 'CTA Text'
        },
        <NICmsContentField.IChoiceField>{
            key: 'ctaStyleAfterCountdown',
            type: NICmsContentField.EFieldType.choice,
            title: 'CTA Style',
            options: ButtonsOptions,
            default: 'button-inline-1'
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-overlay',
            title: 'Overlay text'
        },
        ...NTextOverlay.getFields()
    ]
})
export class PageComponentCountdownBannerComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    component?: IPageComponentCountdownBannerData;

    @Input()
    variant?: string;

    readonly EHorizontalAlignmentVariant = EHorizontalAlignmentVariant;
    readonly EVerticalAlignmentVariant = EVerticalAlignmentVariant;
    protected isActive = false;

    private customerModel = inject(CustomerModel);

    override ngOnInit(): void {
        this.subscriptions.add(
            (this as NICmsContentComponent.IComponent).iCmsValidation$!.subscribe(event => {
                if (event) {
                    if (!this.isAlloweForCustomerGroup()) {
                        event.error = {
                            level: 'info',
                            copy: 'Countdown is not visible for the current customer group'
                        };
                    }
                }
            })
        );
        this.subscriptions.add(this.client.isCms$.subscribe(() => (this as NICmsContentComponent.IComponent).iCmsValidation$!.next()));
        super.ngOnInit();
    }

    protected isActiveHandle(active: boolean) {
        if (this.isActive !== active) {
            this.isActive = active;
            this.detectChanges();
        }
    }

    private isAlloweForCustomerGroup(): boolean {
        if (this.client.isCms) {
            return true;
        }
        if (!this.component?.limitVisibility) {
            return true;
        }
        const groupdId = this.customerModel.data?.group_id?.toString();
        return (groupdId && this.component.eligibleGroupsIds?.split(',').includes(groupdId)) || false;
    }
}
